import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Add, AutoFixHigh, CloudUpload, Delete, Save } from '@mui/icons-material';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  Connection,
  Node,
  Edge,
  MarkerType,
  ReactFlowProvider,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { deviceApi, flowApi, layoutApi, signalsApi } from '../api';
import type { FlowDeployPreview } from '../api';
import { DataTable } from '../table';
import type { DataTableColumn } from '../table';
import { slugCatalogName } from '../files/catalogPaths';
import { displayName } from '../types';
import { useI18n } from '../i18n';
import { datapointsOf, normalizeLayout } from '../layout/normalize';
import { LayoutDefinition } from '../layout/types';
import { FleetEntry, loadFleet } from '../controllers/fleet';
import {
  CatalogEdge,
  CatalogFlow,
  CatalogNode,
  controllerFleetId,
  defaultCatalogFlow,
  nextCatalogId,
  normalizeCatalogNode,
  parseCatalogFlow,
  portRef,
  serializeCatalogFlow,
  splitPortRef,
} from './catalogDoc';
import { CALC_OPS } from './calcOp';
import { portsCompatible, portsForNode } from './catalogPorts';
import { ControllerSnapshot, snapshotFromSettings } from './peripherySnapshot';
import { CATEGORY_COLOR, PalettePayload } from './catalogPreview';
import { flowNodeTypes } from './FlowNodeView';
import FlowPalette from './FlowPalette';
import FlowWizardDialog from './FlowWizardDialog';
import { FlowWizardArgs, mergeFlowWizard } from './catalogFlowWizard';

type LayoutTarget = { layoutId: string; title: string; widgetId: string };

function toRfNodes(nodes: CatalogNode[]): Node<CatalogNode>[] {
  return nodes.map((node) => ({
    id: node.id,
    type: node.type,
    position: { x: node.x, y: node.y },
    data: node,
  }));
}

function cableBothWays(
  src: CatalogNode | undefined,
  sourceHandle: string | null | undefined,
  tgt: CatalogNode | undefined,
  targetHandle: string | null | undefined,
  nodes: Node<CatalogNode>[],
): boolean {
  if (!src || !tgt) {
    return false;
  }
  const from = String(sourceHandle || '');
  const to = String(targetHandle || '');
  if (tgt.type === 'dashboard' && tgt.widgetType === 'relay' && /^ch(\d+)$/.test(to)) {
    const index = Number(to.slice(2)) - 1;
    return tgt.channelReadOnly?.[index] !== true;
  }
  if (tgt.type === 'dashboard' && (tgt.widgetType === 'stacklight' || tgt.widgetType === 'switch') && to === 'in') {
    return tgt.icon !== 'bulb';
  }
  if (src.type === 'device' && src.deviceType === 'zigbee' && from === 'out'
    && tgt.type === 'point' && to === 'command') {
    return true;
  }
  if (src.type === 'point' && from === 'value' && tgt.type === 'mqtt_topic' && to === 'publish') {
    return nodes.some((node) => {
      const data = node.data;
      if (data.type !== 'dashboard') {
        return false;
      }
      if (data.widgetType === 'stacklight') {
        return true;
      }
      if (data.widgetType === 'switch') {
        return data.icon !== 'bulb';
      }
      return data.widgetType === 'relay';
    });
  }
  return false;
}

function toRfEdges(edges: CatalogEdge[]): Edge[] {
  return edges.map((edge, index) => {
    const from = splitPortRef(edge.from);
    const to = splitPortRef(edge.to);
    const both = edge.both === true;
    return {
      id: `e-${index}-${from.id}-${to.id}`,
      source: from.id,
      sourceHandle: from.port,
      target: to.id,
      targetHandle: to.port,
      data: both ? { both: true } : undefined,
      markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14 },
      markerStart: both ? { type: MarkerType.ArrowClosed, width: 14, height: 14 } : undefined,
    };
  });
}

function fromRf(nodes: Node<CatalogNode>[], edges: Edge[]): { nodes: CatalogNode[]; edges: CatalogEdge[] } {
  return {
    nodes: nodes.map((node) => normalizeCatalogNode({
      ...node.data,
      id: node.id,
      type: node.data.type,
      x: node.position.x,
      y: node.position.y,
    })),
    edges: edges.map((edge) => {
      const row: CatalogEdge = {
        from: portRef(edge.source, String(edge.sourceHandle || '')),
        to: portRef(edge.target, String(edge.targetHandle || '')),
      };
      const data = edge.data as { both?: boolean } | undefined;
      if (data?.both) {
        row.both = true;
      }
      return row;
    }),
  };
}

function findPort(node: CatalogNode | undefined, handle: string | null | undefined) {
  if (!node) {
    return undefined;
  }
  return portsForNode(node.type, {
    poll: node.poll,
    widgetType: node.widgetType,
    busType: node.busType,
    role: node.role,
    deviceType: node.deviceType,
    channelNames: node.channelNames,
    channelReadOnly: node.channelReadOnly,
    icon: node.icon,
  }).find((port) => port.id === handle);
}

function deployReasonKey(reason: string): string {
  if (reason === 'bundled') return 'flow.deployBundled';
  if (reason === 'disabled') return 'flow.deployDisabled';
  if (reason === 'noRoutes') return 'flow.deployNoRoutes';
  if (reason === 'noControllers') return 'flow.deployNoControllers';
  if (reason === 'noChanges') return 'flow.deployNoChanges';
  return 'flow.deployFailed';
}

type DeployFileRow = { id: string; hostname: string; path: string; action: string };

function deployFileRows(preview: FlowDeployPreview | null): DeployFileRow[] {
  const rows: DeployFileRow[] = [];
  for (const controller of preview?.controllers || []) {
    for (const file of controller.files) {
      rows.push({
        id: `${controller.deviceId}:${file.path}:${file.action}`,
        hostname: controller.hostname,
        path: file.path,
        action: file.action,
      });
    }
  }
  return rows;
}

const FlowCanvas: React.FC = () => {
  const { t } = useI18n();
  const rf = useReactFlow();
  const [catalog, setCatalog] = useState<{ name: string; title?: string }[]>([]);
  const [savedName, setSavedName] = useState<string | null>(null);
  const [flowName, setFlowName] = useState('');
  const [flowEnable, setFlowEnable] = useState(true);
  const [flowContent, setFlowContent] = useState(() => serializeCatalogFlow(defaultCatalogFlow('')));
  const [savedContent, setSavedContent] = useState(flowContent);
  const [legacy, setLegacy] = useState(false);
  const [layoutTargets, setLayoutTargets] = useState<LayoutTarget[]>([]);
  const [signalFiles, setSignalFiles] = useState<{ name: string }[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [nameDialog, setNameDialog] = useState<null | 'new' | 'save'>(null);
  const [nameDraft, setNameDraft] = useState('');
  const [deployOpen, setDeployOpen] = useState(false);
  const [deployPreview, setDeployPreview] = useState<FlowDeployPreview | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [paletteQuery, setPaletteQuery] = useState('');
  const [fleet, setFleet] = useState<FleetEntry[]>([]);
  const [snapshots, setSnapshots] = useState<Record<string, ControllerSnapshot>>({});
  const [nodes, setNodes, onNodesChange] = useNodesState<Node<CatalogNode>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [selected, setSelected] = useState<Node<CatalogNode> | null>(null);

  const dirty = flowContent !== savedContent;

  const commit = useCallback((nextNodes: Node<CatalogNode>[], nextEdges: Edge[], enable = flowEnable) => {
    const graph = fromRf(nextNodes, nextEdges);
    const doc: CatalogFlow = {
      id: flowName,
      enable,
      nodes: graph.nodes,
      edges: graph.edges,
    };
    setFlowContent(serializeCatalogFlow(doc));
  }, [flowEnable, flowName]);

  const applyDoc = useCallback((doc: CatalogFlow, name: string | null, wasLegacy: boolean) => {
    setNodes(toRfNodes(doc.nodes));
    setEdges(toRfEdges(doc.edges));
    setFlowEnable(doc.enable !== false);
    setFlowName(doc.id || name || '');
    setSavedName(name);
    setLegacy(wasLegacy);
    const raw = serializeCatalogFlow({ ...doc, id: doc.id || name || '' });
    setFlowContent(raw);
    setSavedContent(wasLegacy ? '' : raw);
  }, [setEdges, setNodes]);

  const refreshCatalog = useCallback(async () => {
    const [flows, layouts, signals] = await Promise.all([
      flowApi.list(),
      layoutApi.list(),
      signalsApi.list().catch(() => []),
    ]);
    setCatalog(flows);
    setSignalFiles(signals);
    const targets: LayoutTarget[] = [];
    for (const item of layouts) {
      try {
        const layout = normalizeLayout(await layoutApi.get(item.name) as LayoutDefinition);
        const title = layout.metadata?.name || item.title || item.name;
        for (const widgetId of datapointsOf(layout)) {
          targets.push({ layoutId: item.name, title, widgetId });
        }
      } catch {
        // skip
      }
    }
    setLayoutTargets(targets);
  }, []);

  useEffect(() => {
    refreshCatalog().catch((err) => setError(err?.message || t('flow.loadFailed')));
    loadFleet().then(setFleet).catch(() => setFleet([]));
  }, [refreshCatalog, t]);

  const canvasControllers = useMemo(
    () => nodes.filter((node) => node.data.type === 'controller'),
    [nodes],
  );

  const controllerKey = canvasControllers
    .map((node) => controllerFleetId(node.data))
    .filter((id, index, ids) => ids.indexOf(id) === index)
    .sort()
    .join(',');

  useEffect(() => {
    let cancelled = false;
    const ids = controllerKey ? controllerKey.split(',') : [];
    const load = async () => {
      const next: Record<string, ControllerSnapshot> = {};
      for (const fleetId of ids) {
        try {
          const stored = await deviceApi.getStoredFile(fleetId, '/settings.json');
          const parsed = stored?.content ? JSON.parse(stored.content) : {};
          next[fleetId] = snapshotFromSettings(parsed);
        } catch {
          next[fleetId] = { connections: [], devices: [] };
        }
      }
      if (!cancelled) {
        setSnapshots(next);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [controllerKey]);

  const onConnect = useCallback((connection: Connection) => {
    const src = nodes.find((node) => node.id === connection.source);
    const tgt = nodes.find((node) => node.id === connection.target);
    const fromPort = findPort(src?.data, connection.sourceHandle);
    const toPort = findPort(tgt?.data, connection.targetHandle);
    if (!fromPort || !toPort || fromPort.dir !== 'out' || toPort.dir !== 'in' || !portsCompatible(fromPort.kind, toPort.kind)) {
      setError(t('flow.incompatiblePorts'));
      return;
    }
    const both = cableBothWays(src?.data, connection.sourceHandle, tgt?.data, connection.targetHandle, nodes);
    setEdges((eds) => {
      const next = addEdge({
        ...connection,
        data: both ? { both: true } : undefined,
        markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14 },
        markerStart: both ? { type: MarkerType.ArrowClosed, width: 14, height: 14 } : undefined,
      }, eds);
      commit(nodes, next);
      return next;
    });
    setError(null);
  }, [commit, nodes, setEdges, t]);

  const isValidConnection = useCallback((connection: Connection | Edge) => {
    const current = rf.getNodes() as Node<CatalogNode>[];
    const src = current.find((node) => node.id === connection.source);
    const tgt = current.find((node) => node.id === connection.target);
    const fromPort = findPort(src?.data, connection.sourceHandle);
    const toPort = findPort(tgt?.data, connection.targetHandle);
    return Boolean(
      fromPort && toPort
      && fromPort.dir === 'out'
      && toPort.dir === 'in'
      && portsCompatible(fromPort.kind, toPort.kind),
    );
  }, [rf]);

  const addRfGraph = useCallback((added: Node<CatalogNode>[], extra: Edge[] = []) => {
    const nextNodes = [...nodes, ...added];
    const nextEdges = extra.length ? [...edges, ...extra] : edges;
    setNodes(nextNodes);
    if (extra.length) {
      setEdges(nextEdges);
    }
    commit(nextNodes, nextEdges);
  }, [commit, edges, nodes, setEdges, setNodes]);

  const onDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    const raw = event.dataTransfer.getData('application/vcs-catalog')
      || event.dataTransfer.getData('text/plain');
    if (!raw) {
      return;
    }
    let payload: PalettePayload;
    try {
      payload = JSON.parse(raw);
    } catch {
      return;
    }
    const position = rf.screenToFlowPosition({ x: event.clientX, y: event.clientY });
    const catalogNodes = nodes.map((node) => node.data);
    const extra: Edge[] = [];
    const wire = (fromId: string, fromPort: string, toId: string, toPort: string) => {
      extra.push({
        id: `e-${edges.length + extra.length}-${fromId}-${toId}`,
        source: fromId,
        sourceHandle: fromPort,
        target: toId,
        targetHandle: toPort,
      });
    };
    let node: Node<CatalogNode> | null = null;
    if (payload.kind === 'controller') {
      const id = nextCatalogId('g', catalogNodes);
      node = {
        id,
        type: 'controller',
        position,
        data: {
          id,
          type: 'controller',
          x: position.x,
          y: position.y,
          owner: payload.fleetId,
          hostname: payload.hostname,
        },
      };
    } else if (payload.kind === 'bus') {
      const id = nextCatalogId('bus', catalogNodes);
      node = {
        id,
        type: 'bus',
        position,
        data: {
          id,
          type: 'bus',
          x: position.x,
          y: position.y,
          of: payload.of,
          busType: payload.busType,
          role: payload.role,
        },
      };
      const fleet = payload.ownerId.replace(/^g:/, '');
      const parent = nodes.find((item) => (
        item.data.type === 'controller' && controllerFleetId(item.data) === fleet
      ));
      if (parent) {
        wire(parent.id, 'host', id, 'host');
      }
    } else if (payload.kind === 'device') {
      const id = nextCatalogId('dev', catalogNodes);
      const instanceId = payload.of;
      const connection = payload.connection;
      const ownerId = payload.ownerId;
      node = {
        id,
        type: 'device',
        position,
        data: {
          id,
          type: 'device',
          x: position.x,
          y: position.y,
          of: instanceId,
          deviceType: payload.deviceType,
        },
      };
      const parent = nodes.find((item) => (
        item.data.type === 'bus' && item.data.of === connection
      ));
      if (parent) {
        wire(parent.id, 'read', id, 'read');
      } else if (!connection) {
        const fleet = ownerId.replace(/^g:/, '');
        const host = nodes.find((item) => (
          item.data.type === 'controller' && controllerFleetId(item.data) === fleet
        ));
        if (host) {
          wire(host.id, 'host', id, 'host');
        }
      }
    } else if (payload.kind === 'point') {
      const id = nextCatalogId('p', catalogNodes);
      const instanceId = payload.of;
      node = {
        id,
        type: 'point',
        position,
        data: {
          id,
          type: 'point',
          x: position.x,
          y: position.y,
          of: instanceId,
          key: payload.key,
          poll: payload.poll,
        },
      };
      const parent = nodes.find((item) => item.data.type === 'device' && item.data.of === instanceId);
      if (parent) {
        wire(parent.id, 'out', id, 'dev');
      }
    } else if (payload.kind === 'mqtt_topic') {
      const id = nextCatalogId('top', catalogNodes);
      node = {
        id,
        type: 'mqtt_topic',
        position,
        data: {
          id,
          type: 'mqtt_topic',
          x: position.x,
          y: position.y,
          topic: '{base}/',
        },
      };
    } else if (payload.kind === 'zigbee_point') {
      const id = nextCatalogId('zb', catalogNodes);
      node = {
        id,
        type: 'zigbee_point',
        position,
        data: {
          id,
          type: 'zigbee_point',
          x: position.x,
          y: position.y,
          of: 'inputs',
          key: 'button',
        },
      };
    } else if (payload.kind === 'timer') {
      const id = nextCatalogId('tick', catalogNodes);
      node = {
        id,
        type: 'timer',
        position,
        data: { id, type: 'timer', x: position.x, y: position.y, interval: 60 },
      };
    } else if (payload.kind === 'calc') {
      const id = nextCatalogId('calc', catalogNodes);
      node = {
        id,
        type: 'calc',
        position,
        data: { id, type: 'calc', x: position.x, y: position.y, op: '*', value: 0.1 },
      };
    } else if (payload.kind === 'label') {
      const id = nextCatalogId('lbl', catalogNodes);
      node = {
        id,
        type: 'label',
        position,
        data: {
          id,
          type: 'label',
          x: position.x,
          y: position.y,
          layout: payload.layoutId,
          widget: payload.widgetId,
        },
      };
    } else if (payload.kind === 'dashboard') {
      const id = nextCatalogId('dash', catalogNodes);
      node = {
        id,
        type: 'dashboard',
        position,
        data: {
          id,
          type: 'dashboard',
          x: position.x,
          y: position.y,
          widgetType: payload.widgetType,
          title: t('flow.climateWidget'),
        },
      };
    } else if (payload.kind === 'threshold') {
      const id = nextCatalogId('thr', catalogNodes);
      node = {
        id,
        type: 'threshold',
        position,
        data: {
          id,
          type: 'threshold',
          x: position.x,
          y: position.y,
          min: 0,
          max: 40,
          hysteresis: 0.5,
        },
      };
    } else if (payload.kind === 'toggle') {
      const id = nextCatalogId('tg', catalogNodes);
      node = {
        id,
        type: 'toggle',
        position,
        data: {
          id,
          type: 'toggle',
          x: position.x,
          y: position.y,
          edge: 'rising',
          title: t('flow.brick.toggle'),
        },
      };
    } else if (payload.kind === 'notify') {
      const id = nextCatalogId('ntf', catalogNodes);
      node = {
        id,
        type: 'notify',
        position,
        data: {
          id,
          type: 'notify',
          x: position.x,
          y: position.y,
          title: t('flow.brick.notify'),
          criticality: 2,
          channels: ['pushover', 'mail'],
        },
      };
    }
    if (node) {
      addRfGraph([node], extra);
    }
  }, [addRfGraph, edges.length, nodes, rf, t]);

  const involvedHosts = canvasControllers
    .map((node) => node.data.hostname || controllerFleetId(node.data))
    .filter((host, index, hosts) => Boolean(host) && hosts.indexOf(host) === index);
  const deployRows = useMemo(() => deployFileRows(deployPreview), [deployPreview]);
  const deployColumns = useMemo((): DataTableColumn<DeployFileRow>[] => [
    { id: 'hostname', label: t('flow.controller'), accessor: (row) => row.hostname, filter: 'none' },
    { id: 'path', label: t('flow.deployFile'), accessor: (row) => row.path, filter: 'none' },
    {
      id: 'action',
      label: t('flow.deployAction'),
      accessor: (row) => row.action,
      filter: 'none',
      render: (row) => t(
        row.action === 'create'
          ? 'flow.deployCreate'
          : row.action === 'delete'
            ? 'flow.deployDelete'
            : 'flow.deployUpdate',
      ),
    },
  ], [t]);
  const deployBlocked = Boolean(
    deployPreview?.reason === 'bundled'
    || deployPreview?.reason === 'disabled'
    || deployPreview?.reason === 'noControllers'
    || deployPreview?.reason === 'noRoutes',
  );

  const confirmDiscard = () => !dirty || window.confirm(t('flow.unsavedConfirm'));

  const handleLoad = async (name: string) => {
    if (!name || !confirmDiscard()) {
      return;
    }
    try {
      setBusy(true);
      const raw = await flowApi.get(name);
      const parsed = parseCatalogFlow(raw, name);
      applyDoc(parsed.doc, name, parsed.legacy);
      setStatus(parsed.legacy ? null : t('flow.loaded', { name }));
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || t('flow.loadFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleNew = () => {
    setNameDraft('');
    setNameDialog('new');
  };

  const saveDraft = async (rawName: string): Promise<boolean> => {
    if (!rawName.trim()) {
      setError(t('flow.nameRequired'));
      return false;
    }
    const name = slugCatalogName(rawName);
    try {
      setBusy(true);
      const graph = fromRf(nodes, edges);
      const doc: CatalogFlow = { id: name, enable: flowEnable, nodes: graph.nodes, edges: graph.edges };
      await flowApi.save(name, JSON.parse(serializeCatalogFlow(doc)));
      const raw = serializeCatalogFlow(doc);
      setFlowName(name);
      setSavedName(name);
      setFlowContent(raw);
      setSavedContent(raw);
      setLegacy(false);
      await refreshCatalog();
      setStatus(t('flow.savedDraft'));
      setError(null);
      return true;
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || t('flow.saveFailed'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    if (!flowName.trim()) {
      setNameDraft('');
      setNameDialog('save');
      return;
    }
    await saveDraft(flowName);
  };

  const submitNameDialog = async () => {
    const typed = nameDraft.trim();
    if (!typed) {
      return;
    }
    const name = slugCatalogName(typed);
    if (nameDialog === 'new') {
      if (!confirmDiscard()) {
        return;
      }
      applyDoc(defaultCatalogFlow(name), null, false);
      setFlowName(name);
      setStatus(t('flow.created'));
      setError(null);
      setNameDialog(null);
      return;
    }
    setNameDialog(null);
    await saveDraft(name);
  };

  const catalogPayload = () => {
    const graph = fromRf(nodes, edges);
    return JSON.parse(serializeCatalogFlow({
      id: savedName || flowName,
      enable: flowEnable,
      nodes: graph.nodes,
      edges: graph.edges,
    })) as Record<string, unknown>;
  };

  const handleDeploy = async () => {
    if (!savedName) {
      setError(t('flow.deployNeedDraft'));
      return;
    }
    try {
      setBusy(true);
      setError(null);
      if (dirty) {
        const ok = await saveDraft(savedName);
        if (!ok) {
          return;
        }
      }
      const preview = await flowApi.previewDeploy(savedName, catalogPayload());
      setDeployPreview(preview);
      setDeployOpen(true);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || t('flow.deployFailed'));
    } finally {
      setBusy(false);
    }
  };

  const confirmDeploy = async () => {
    if (!savedName) {
      return;
    }
    try {
      setBusy(true);
      const result = await flowApi.deploy(savedName, catalogPayload());
      setDeployOpen(false);
      const jobs = result.jobs || [];
      const widgets = result.widgets || [];
      if (jobs.length) {
        setStatus(t('flow.deployQueued', { count: jobs.length }));
        setError(null);
      } else if (widgets.length && !result.reason) {
        setStatus(t('flow.deployWidgets', { count: widgets.length }));
        setError(null);
      } else if (result.reason === 'noChanges') {
        setStatus(t('flow.deployNoChanges'));
        setError(null);
      } else if (result.reason) {
        setError(t(deployReasonKey(result.reason)));
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || t('flow.deployFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!savedName || !window.confirm(t('flow.deleteConfirm', { name: savedName }))) {
      return;
    }
    try {
      setBusy(true);
      await flowApi.remove(savedName);
      applyDoc(defaultCatalogFlow(''), null, false);
      await refreshCatalog();
      setStatus(t('flow.deleted'));
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || t('flow.deleteFailed'));
    } finally {
      setBusy(false);
    }
  };

  const selectedNode = selected ? nodes.find((node) => node.id === selected.id) || selected : null;

  const patchSelected = (patch: Partial<CatalogNode>) => {
    if (!selectedNode) {
      return;
    }
    const next = nodes.map((node) => (
      node.id === selectedNode.id
        ? { ...node, data: { ...node.data, ...patch } }
        : node
    ));
    setNodes(next);
    commit(next, edges);
  };

  const setRelayChannelReadOnly = (index: number, readOnly: boolean) => {
    if (!selectedNode) {
      return;
    }
    const flags = Array.from({ length: 8 }, (_, i) => selectedNode.data.channelReadOnly?.[i] === true);
    flags[index] = readOnly;
    const handle = `ch${index + 1}`;
    const into = edges.find((edge) => edge.target === selectedNode.id && edge.targetHandle === handle);
    let pointId = '';
    if (into) {
      const src = nodes.find((node) => node.id === into.source);
      if (src?.data.type === 'point') {
        pointId = src.id;
      } else if (src?.data.type === 'mqtt_topic') {
        const pub = edges.find((edge) => edge.target === src.id && edge.targetHandle === 'publish' && edge.sourceHandle === 'value');
        pointId = pub?.source || '';
      }
    }
    const both = !readOnly;
    const nextEdges = edges.map((edge) => {
      const touchesChannel = edge.target === selectedNode.id && edge.targetHandle === handle;
      const touchesPoint = pointId && (
        (edge.source === pointId && edge.sourceHandle === 'value' && edge.targetHandle === 'publish')
        || (edge.target === pointId && edge.targetHandle === 'command' && edge.sourceHandle === 'out')
      );
      if (!touchesChannel && !touchesPoint) {
        return edge;
      }
      return {
        ...edge,
        data: both ? { both: true } : undefined,
        markerStart: both ? { type: MarkerType.ArrowClosed, width: 14, height: 14 } : undefined,
      };
    });
    const nextNodes = nodes.map((node) => (
      node.id === selectedNode.id
        ? { ...node, data: { ...node.data, channelReadOnly: flags } }
        : node
    ));
    setNodes(nextNodes);
    setEdges(nextEdges);
    commit(nextNodes, nextEdges);
  };

  const removeSelected = () => {
    if (!selectedNode) {
      return;
    }
    const nextNodes = nodes.filter((node) => node.id !== selectedNode.id);
    const nextEdges = edges.filter((edge) => edge.source !== selectedNode.id && edge.target !== selectedNode.id);
    setNodes(nextNodes);
    setEdges(nextEdges);
    setSelected(null);
    commit(nextNodes, nextEdges);
  };

  const wizardController = useMemo(() => {
    const first = canvasControllers[0];
    if (!first) {
      return null;
    }
    const fleetId = controllerFleetId(first.data);
    return fleet.filter((entry) => entry.id === fleetId || entry.hostname === first.data.hostname)[0] || {
      id: fleetId,
      hostname: first.data.hostname || fleetId,
      online: false,
    };
  }, [canvasControllers, fleet]);

  const insertWizard = (args: FlowWizardArgs) => {
    const graph = fromRf(nodes, edges);
    const doc = mergeFlowWizard({
      id: flowName,
      enable: flowEnable,
      nodes: graph.nodes,
      edges: graph.edges,
    }, args);
    const nextNodes = toRfNodes(doc.nodes);
    const nextEdges = toRfEdges(doc.edges);
    setNodes(nextNodes);
    setEdges(nextEdges);
    commit(nextNodes, nextEdges);
    setWizardOpen(false);
    setStatus(t('flow.chainInserted'));
    setError(null);
    window.setTimeout(() => rf.fitView({ padding: 0.2 }), 50);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%', overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {dirty && <Typography variant="body2" color="warning.main">*</Typography>}
          <Typography variant="subtitle1" sx={{ minWidth: 120 }}>
            {flowName || t('flow.unnamed')}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('flow.openSaved')}</InputLabel>
            <Select label={t('flow.openSaved')} value={savedName || ''} onChange={(e) => handleLoad(String(e.target.value))}>
              <MenuItem value=""><em>{t('flow.openSaved')}</em></MenuItem>
              {catalog.map((item) => (
                <MenuItem key={item.name} value={item.name}>{item.title || item.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Divider orientation="vertical" flexItem />
          <Button size="small" startIcon={<Add />} onClick={handleNew} disabled={busy}>{t('common.newItem')}</Button>
          <Button size="small" startIcon={<AutoFixHigh />} onClick={() => setWizardOpen(true)} disabled={busy}>
            {t('flow.chainWizard')}
          </Button>
          <Button size="small" variant="contained" startIcon={<Save />} onClick={handleSave} disabled={busy}>{t('flow.saveDraft')}</Button>
          <Button size="small" variant="outlined" startIcon={<CloudUpload />} onClick={handleDeploy} disabled={busy || !savedName}>{t('flow.deploy')}</Button>
          <Button size="small" color="error" startIcon={<Delete />} onClick={handleDelete} disabled={busy || !savedName}>{t('common.delete')}</Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {t('flow.hintEditorOnly')}
        </Typography>
      </Box>
      {error && <Alert severity="error" sx={{ mx: 2, mt: 1 }}>{error}</Alert>}
      {legacy && !error && <Alert severity="warning" sx={{ mx: 2, mt: 1 }}>{t('flow.legacyHint')}</Alert>}
      {status && <Alert severity="success" sx={{ mx: 2, mt: 1 }} onClose={() => setStatus(null)}>{status}</Alert>}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, px: 2, py: 2, gap: 1 }}>
        <Paper sx={{ width: 320, p: 1, overflow: 'auto' }}>
          <FlowPalette
            query={paletteQuery}
            onQuery={setPaletteQuery}
            fleet={fleet}
            canvasControllers={canvasControllers.map((node) => ({
              id: controllerFleetId(node.data),
              hostname: node.data.hostname || controllerFleetId(node.data),
            }))}
            snapshots={snapshots}
            layoutTargets={layoutTargets}
          />
        </Paper>
        <Box
          sx={{ flex: 1, minWidth: 0, minHeight: 0, height: '100%', position: 'relative', border: 1, borderColor: 'divider', borderRadius: 1 }}
          onDragOver={(event) => event.preventDefault()}
          onDrop={onDrop}
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeDragStop={() => commit(rf.getNodes() as Node<CatalogNode>[], rf.getEdges())}
            onConnect={onConnect}
            isValidConnection={isValidConnection}
            onSelectionChange={({ nodes: sel }) => setSelected((sel[0] as Node<CatalogNode> | undefined) || null)}
            nodeTypes={flowNodeTypes}
            fitView
            deleteKeyCode={['Backspace', 'Delete']}
            onNodesDelete={(gone) => {
              const ids = new Set(gone.map((node) => node.id));
              const nextNodes = nodes.filter((node) => !ids.has(node.id));
              const nextEdges = edges.filter((edge) => !ids.has(edge.source) && !ids.has(edge.target));
              if (selected && ids.has(selected.id)) {
                setSelected(null);
              }
              commit(nextNodes, nextEdges);
            }}
            onEdgesDelete={(gone) => {
              const ids = new Set(gone.map((edge) => edge.id));
              commit(nodes, edges.filter((edge) => !ids.has(edge.id)));
            }}
            style={{ width: '100%', height: '100%' }}
          >
            <Background />
            <Controls />
            <MiniMap nodeColor={(node) => CATEGORY_COLOR[String(node.type)] || '#90a4ae'} />
          </ReactFlow>
        </Box>
        <Paper sx={{ width: 280, p: 1.5, overflow: 'auto' }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('flow.inspector')}</Typography>
          <FormControlLabel
            control={(
              <Checkbox
                size="small"
                checked={flowEnable}
                onChange={(e) => {
                  setFlowEnable(e.target.checked);
                  commit(nodes, edges, e.target.checked);
                }}
              />
            )}
            label={t('flow.fileEnable')}
          />
          {!selectedNode && (
            <Typography variant="body2" color="text.secondary">{t('flow.inspectorEmptyNew')}</Typography>
          )}
          {selectedNode?.data.type === 'calc' && (
            <>
              <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                <InputLabel>{t('flow.calcOp')}</InputLabel>
                <Select
                  label={t('flow.calcOp')}
                  value={selectedNode.data.op || '*'}
                  onChange={(e) => patchSelected({ op: String(e.target.value) })}
                >
                  {CALC_OPS.map((op) => (
                    <MenuItem key={op} value={op}>{op}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                fullWidth
                type="number"
                label={t('flow.calcValue')}
                value={selectedNode.data.value ?? 0.1}
                onChange={(e) => patchSelected({ value: Number(e.target.value) })}
                sx={{ mt: 1 }}
              />
            </>
          )}
          {selectedNode?.data.type === 'timer' && (
            <TextField
              size="small"
              fullWidth
              type="number"
              label={t('flow.intervalSec')}
              value={selectedNode.data.interval ?? 60}
              onChange={(e) => patchSelected({ interval: Number(e.target.value) || 0 })}
              sx={{ mt: 1 }}
            />
          )}
          {selectedNode?.data.type === 'mqtt_topic' && (
            <TextField
              size="small"
              fullWidth
              label={t('flow.mqttTopic')}
              value={selectedNode.data.topic || ''}
              onChange={(e) => patchSelected({ topic: e.target.value })}
              sx={{ mt: 1 }}
              helperText={t('flow.topicUsesBase')}
            />
          )}
          {selectedNode?.data.type === 'zigbee_point' && (
            <>
              <TextField
                size="small"
                fullWidth
                label="of"
                value={selectedNode.data.of || ''}
                onChange={(e) => patchSelected({ of: e.target.value })}
                sx={{ mt: 1 }}
              />
              <TextField
                size="small"
                fullWidth
                label="key"
                value={selectedNode.data.key || ''}
                onChange={(e) => patchSelected({ key: e.target.value })}
                sx={{ mt: 1 }}
                helperText={t('flow.zigbeePointHint')}
              />
            </>
          )}
          {selectedNode?.data.type === 'dashboard' && (
            <>
            <TextField
              size="small"
              fullWidth
              label={t('flow.widgetTitle')}
              value={selectedNode.data.title || ''}
              onChange={(e) => patchSelected({ title: e.target.value })}
              sx={{ mt: 1 }}
            />
            {selectedNode.data.widgetType === 'relay' && Array.from({ length: 8 }).map((_, index) => {
              const wired = edges.some((edge) => edge.target === selectedNode.id && edge.targetHandle === `ch${index + 1}`);
              const readOnly = selectedNode.data.channelReadOnly?.[index] === true
                || (wired && !edges.some((edge) => edge.target === selectedNode.id && edge.targetHandle === `ch${index + 1}` && (edge.data as { both?: boolean } | undefined)?.both));
              return (
                <Box key={`ch${index + 1}`} sx={{ mt: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    label={t(readOnly ? 'flow.port.channelBulb' : 'flow.port.channelSwitch', { n: index + 1 })}
                    value={(selectedNode.data.channelNames || [])[index] || ''}
                    onChange={(e) => {
                      const next = Array.from({ length: 8 }, (_, i) => (selectedNode.data.channelNames || [])[i] || '');
                      next[index] = e.target.value;
                      patchSelected({ channelNames: next });
                    }}
                  />
                  <FormControlLabel
                    control={(
                      <Checkbox
                        size="small"
                        checked={readOnly}
                        onChange={(e) => setRelayChannelReadOnly(index, e.target.checked)}
                      />
                    )}
                    label={t('flow.channelReadOnly')}
                  />
                </Box>
              );
            })}
            </>
          )}
          {selectedNode?.data.type === 'threshold' && (
            <>
              <TextField
                size="small"
                fullWidth
                label={t('flow.widgetTitle')}
                value={selectedNode.data.title || ''}
                onChange={(e) => patchSelected({ title: e.target.value })}
                sx={{ mt: 1 }}
              />
              <TextField
                size="small"
                fullWidth
                label={t('alerting.match')}
                value={selectedNode.data.match || ''}
                onChange={(e) => patchSelected({ match: e.target.value })}
                helperText={t('alerting.matchHint')}
                sx={{ mt: 1 }}
              />
              <TextField
                size="small"
                fullWidth
                label={t('alerting.field')}
                value={selectedNode.data.field || ''}
                onChange={(e) => patchSelected({ field: e.target.value })}
                helperText={t('alerting.fieldHint')}
                sx={{ mt: 1 }}
              />
              <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                <InputLabel>{t('alerting.edge')}</InputLabel>
                <Select
                  label={t('alerting.edge')}
                  value={selectedNode.data.edge === 'enter' || selectedNode.data.edge === 'leave'
                    ? selectedNode.data.edge
                    : 'both'}
                  onChange={(e) => patchSelected({ edge: String(e.target.value) })}
                >
                  <MenuItem value="both">{t('alerting.edgeBoth')}</MenuItem>
                  <MenuItem value="enter">{t('alerting.edgeEnter')}</MenuItem>
                  <MenuItem value="leave">{t('alerting.edgeLeave')}</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                fullWidth
                type="number"
                label={t('alerting.min')}
                value={selectedNode.data.min ?? ''}
                onChange={(e) => patchSelected({
                  min: e.target.value === '' ? undefined : Number(e.target.value),
                })}
                sx={{ mt: 1 }}
              />
              <TextField
                size="small"
                fullWidth
                type="number"
                label={t('alerting.max')}
                value={selectedNode.data.max ?? ''}
                onChange={(e) => patchSelected({
                  max: e.target.value === '' ? undefined : Number(e.target.value),
                })}
                sx={{ mt: 1 }}
              />
              <TextField
                size="small"
                fullWidth
                type="number"
                label={t('alerting.hysteresis')}
                value={selectedNode.data.hysteresis ?? 0}
                onChange={(e) => patchSelected({ hysteresis: Number(e.target.value) || 0 })}
                sx={{ mt: 1 }}
              />
              <TextField
                size="small"
                fullWidth
                type="number"
                label={t('alerting.alarmDelay')}
                value={selectedNode.data.alarmDelayMin ?? 0}
                onChange={(e) => patchSelected({ alarmDelayMin: Number(e.target.value) || 0 })}
                sx={{ mt: 1 }}
              />
              <TextField
                size="small"
                fullWidth
                type="number"
                label={t('alerting.resetDelay')}
                value={selectedNode.data.resetDelayMin ?? 0}
                onChange={(e) => patchSelected({ resetDelayMin: Number(e.target.value) || 0 })}
                sx={{ mt: 1 }}
              />
            </>
          )}
          {selectedNode?.data.type === 'toggle' && (
            <>
              <TextField
                size="small"
                fullWidth
                label={t('flow.widgetTitle')}
                value={selectedNode.data.title || ''}
                onChange={(e) => patchSelected({ title: e.target.value })}
                sx={{ mt: 1 }}
              />
              <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                <InputLabel>{t('flow.toggleEdge')}</InputLabel>
                <Select
                  label={t('flow.toggleEdge')}
                  value={selectedNode.data.edge === 'falling' ? 'falling' : 'rising'}
                  onChange={(e) => patchSelected({ edge: String(e.target.value) })}
                >
                  <MenuItem value="rising">{t('flow.toggleRising')}</MenuItem>
                  <MenuItem value="falling">{t('flow.toggleFalling')}</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {t('flow.toggleHint')}
              </Typography>
            </>
          )}
          {selectedNode?.data.type === 'notify' && (
            <>
              <TextField
                size="small"
                fullWidth
                label={t('flow.widgetTitle')}
                value={selectedNode.data.title || ''}
                onChange={(e) => patchSelected({ title: e.target.value })}
                sx={{ mt: 1 }}
              />
              <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                <InputLabel>{t('alerting.criticality')}</InputLabel>
                <Select
                  label={t('alerting.criticality')}
                  value={selectedNode.data.criticality ?? 2}
                  onChange={(e) => patchSelected({ criticality: Number(e.target.value) })}
                >
                  <MenuItem value={1}>1</MenuItem>
                  <MenuItem value={2}>2</MenuItem>
                  <MenuItem value={3}>3</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={(
                  <Checkbox
                    size="small"
                    checked={(selectedNode.data.channels || []).indexOf('pushover') >= 0}
                    onChange={(e) => {
                      const prev = Array.isArray(selectedNode.data.channels)
                        ? selectedNode.data.channels
                        : [];
                      const next = e.target.checked
                        ? prev.concat('pushover').filter((c, i, arr) => arr.indexOf(c) === i)
                        : prev.filter((c) => c !== 'pushover');
                      patchSelected({ channels: next });
                    }}
                  />
                )}
                label={t('alerting.pushoverEnable')}
                sx={{ mt: 1, display: 'flex' }}
              />
              <FormControlLabel
                control={(
                  <Checkbox
                    size="small"
                    checked={(selectedNode.data.channels || []).indexOf('mail') >= 0}
                    onChange={(e) => {
                      const prev = Array.isArray(selectedNode.data.channels)
                        ? selectedNode.data.channels
                        : [];
                      const next = e.target.checked
                        ? prev.concat('mail').filter((c, i, arr) => arr.indexOf(c) === i)
                        : prev.filter((c) => c !== 'mail');
                      patchSelected({ channels: next });
                    }}
                  />
                )}
                label={t('alerting.mailEnable')}
                sx={{ display: 'flex' }}
              />
            </>
          )}
          {selectedNode?.data.type === 'controller' && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {displayName({
                id: controllerFleetId(selectedNode.data),
                hostname: selectedNode.data.hostname || '',
              })}
            </Typography>
          )}
          {selectedNode?.data.type === 'bus' && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {selectedNode.data.busType}{selectedNode.data.role ? ` ${selectedNode.data.role}` : ''} · {selectedNode.data.of}
            </Typography>
          )}
          {selectedNode?.data.type === 'device' && (
            <Typography variant="body2" sx={{ mt: 1 }}>{selectedNode.data.deviceType} · {selectedNode.data.of}</Typography>
          )}
          {selectedNode?.data.type === 'device' && selectedNode.data.deviceType === 'stacklight' && (
            <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
              <InputLabel>{t('flow.stacklightSignal')}</InputLabel>
              <Select
                label={t('flow.stacklightSignal')}
                value={selectedNode.data.signal || ''}
                onChange={(e) => patchSelected({ signal: String(e.target.value) || undefined })}
              >
                <MenuItem value=""><em>{t('flow.stacklightSignalNone')}</em></MenuItem>
                {signalFiles.map((item) => (
                  <MenuItem key={item.name} value={item.name}>{item.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {selectedNode?.data.type === 'point' && (
            <Typography variant="body2" sx={{ mt: 1 }}>{selectedNode.data.of}.{selectedNode.data.key}</Typography>
          )}
          {selectedNode && (
            <Button size="small" color="error" startIcon={<Delete />} sx={{ mt: 1.5 }} onClick={removeSelected}>
              {t('flow.removeNode')}
            </Button>
          )}
        </Paper>
      </Box>
      <Dialog open={Boolean(nameDialog)} onClose={() => setNameDialog(null)} fullWidth maxWidth="xs">
        <DialogTitle>{nameDialog === 'new' ? t('flow.newTitle') : t('flow.saveDraft')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            label={t('common.name')}
            value={nameDraft}
            onChange={(event) => setNameDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submitNameDialog();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNameDialog(null)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={submitNameDialog} disabled={busy || !nameDraft.trim()}>
            {nameDialog === 'new' ? t('common.create') : t('flow.saveDraft')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deployOpen} onClose={() => setDeployOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('flow.deployTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>{t('flow.deployHint')}</Typography>
          {deployPreview?.reason && (
            <Alert severity={deployPreview.reason === 'noChanges' ? 'info' : 'warning'} sx={{ mb: 1 }}>
              {t(deployReasonKey(deployPreview.reason))}
            </Alert>
          )}
          {Boolean(deployPreview?.widgets?.length) && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('flow.deployWidgets', { count: deployPreview?.widgets?.length || 0 })}
            </Typography>
          )}
          {deployRows.length > 0 ? (
            <DataTable
              rows={deployRows}
              columns={deployColumns}
              getRowId={(row) => row.id}
              toolbar={false}
              dense
              emptyMessage={t('flow.deployEmptyHosts')}
            />
          ) : involvedHosts.length === 0 && !deployPreview?.widgets?.length ? (
            <Typography variant="body2" color="text.secondary">{t('flow.deployEmptyHosts')}</Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeployOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" startIcon={<CloudUpload />} onClick={confirmDeploy} disabled={busy || deployBlocked}>{t('flow.deploy')}</Button>
        </DialogActions>
      </Dialog>
      <FlowWizardDialog
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        fleet={fleet}
        initialController={wizardController}
        layoutTargets={layoutTargets}
        flowName={flowName}
        onInsert={insertWizard}
      />
    </Box>
  );
};

const FlowEditor: React.FC = () => (
  <ReactFlowProvider>
    <FlowCanvas />
  </ReactFlowProvider>
);

export default FlowEditor;
